/**
 * Finance Quotes tab — rough EMI quotations (scheme-driven flat interest).
 * Schemes mirror FINANCE_PRODUCT (90FINSPECIAL12 = 10% down / 12m / 12% flat).
 * Endpoints live under /finance/*. Editor computes live via /finance/quotes/compute.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Printer, WhatsappLogo, PencilSimple, Trash, X, UploadSimple } from "@phosphor-icons/react";
import { FinanceSchemePicker } from "@/components/FinanceSchemePicker";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_COLORS = {
  DRAFT:     "bg-canvas-line/40 text-ink",
  SHARED:    "bg-blue-100 text-blue-800",
  CONVERTED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-rose-100 text-rose-700",
};

const EMPTY_FORM = {
  vehicle_ref_id: "", vehicle_model: "", vehicle_price: "",
  hypothecation: 500, hypothecation_yes: true,
  ext_warranty: 999, ext_warranty_yes: true,
  scheme_id: "", down_payment: "",
  customer_name: "", customer_phone: "", customer_address: "",
  notes: "",
};

const SCHEME_FIELDS = [
  ["product_id", "Product ID", "text"],
  ["down_payment_pct", "Down Pay % (0.10 = 10%)", "number"],
  ["processing_charge_pct", "Processing % (0.02 = 2%)", "number"],
  ["stamp_duty", "Stamp Duty ₹", "number"],
  ["selling_charges", "Selling Charges ₹", "number"],
  ["application_charge", "Application Charge ₹", "number"],
  ["gst", "GST ₹", "number"],
  ["insurance", "Insurance ₹", "number"],
  ["dealer_incentive", "Dealer Incentive ₹", "number"],
  ["dealer_subvention", "Dealer Subvention ₹", "number"],
  ["tenure", "Tenure (months)", "number"],
  ["rate_interest", "Flat Rate % p.a.", "number"],
];

function SchemeManager({ onClose, onChanged }) {
  const [schemes, setSchemes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/schemes");
      setSchemes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...editing };
      for (const [k] of SCHEME_FIELDS) {
        if (k !== "product_id") body[k] = Number(body[k]) || 0;
      }
      if (editing.id) await api.put(`/finance/schemes/${editing.id}`, body);
      else await api.post("/finance/schemes", body);
      toast.success("Scheme saved");
      setEditing(null);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete scheme ${s.product_id}?`)) return;
    try {
      await api.delete(`/finance/schemes/${s.id}`);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const b64 = String(reader.result).split(",")[1];
        const { data } = await api.post("/finance/schemes/import", { file_b64: b64, filename: file.name });
        toast.success(`Imported — ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
        load();
        onChanged && onChanged();
      } catch (err) { toast.error(formatApiError(err)); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="scheme-manager">
      <div className="bg-canvas border border-canvas-line w-full max-w-4xl max-h-[85vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">Finance Schemes</div>
          <div className="flex items-center gap-2">
            <label className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink cursor-pointer flex items-center gap-1.5">
              <UploadSimple size={12} /> Import XLSX
              <input type="file" accept=".xlsx" className="hidden" onChange={importFile} data-testid="scheme-import-input" />
            </label>
            <button onClick={() => setEditing({ active: true })} data-testid="scheme-add"
              className="px-3 py-1.5 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5">
              <Plus size={12} /> Add
            </button>
            <button onClick={onClose} data-testid="scheme-manager-close" className="p-1.5 hover:bg-canvas-panel"><X size={14} /></button>
          </div>
        </div>

        {editing && (
          <div className="border border-canvas-line p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-canvas-panel/40" data-testid="scheme-editor">
            {SCHEME_FIELDS.map(([k, label, type]) => (
              <label key={k} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</span>
                <input
                  type={type}
                  value={editing[k] ?? ""}
                  onChange={(e) => setEditing((f) => ({ ...f, [k]: e.target.value }))}
                  data-testid={`scheme-field-${k}`}
                  className="px-2 py-1.5 bg-canvas border border-canvas-line text-sm"
                />
              </label>
            ))}
            <div className="col-span-full flex gap-2">
              <button onClick={save} data-testid="scheme-save" className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand">Save</button>
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink">Cancel</button>
            </div>
          </div>
        )}

        <div className="border border-canvas-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                {["Product", "Down %", "Tenure", "Rate %", "Processing %", "Stamp", "Selling", "Insur.", "Incentive", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schemes.map((s) => (
                <tr key={s.id} className="border-b border-canvas-line/50" data-testid={`scheme-row-${s.product_id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{s.product_id}</td>
                  <td className="px-3 py-1.5 font-mono">{Math.round((s.down_payment_pct || 0) * 100)}%</td>
                  <td className="px-3 py-1.5 font-mono">{s.tenure}m</td>
                  <td className="px-3 py-1.5 font-mono">{s.rate_interest}%</td>
                  <td className="px-3 py-1.5 font-mono">{Math.round((s.processing_charge_pct || 0) * 100)}%</td>
                  <td className="px-3 py-1.5 font-mono">{inr(s.stamp_duty)}</td>
                  <td className="px-3 py-1.5 font-mono">{inr(s.selling_charges)}</td>
                  <td className="px-3 py-1.5 font-mono">{inr(s.insurance)}</td>
                  <td className="px-3 py-1.5 font-mono">{inr(s.dealer_incentive)}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(s)} data-testid={`scheme-edit-${s.product_id}`} className="p-1 hover:bg-canvas-panel"><PencilSimple size={13} /></button>
                      <button onClick={() => remove(s)} data-testid={`scheme-delete-${s.product_id}`} className="p-1 hover:bg-rose-50 text-rose-600"><Trash size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {schemes.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-ink-muted text-xs">No schemes — import the FINANCE_PRODUCT sheet or add one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function QuoteEditor({ initial, schemes, pricelist, branches, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [computed, setComputed] = useState(null);
  const [busy, setBusy] = useState(false);

  const scheme = useMemo(() => schemes.find((s) => s.id === form.scheme_id), [schemes, form.scheme_id]);

  // Live compute — down payment auto-fills from scheme % until manually edited
  useEffect(() => {
    if (!scheme || !Number(form.vehicle_price)) { setComputed(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/finance/quotes/compute", {
          scheme_id: scheme.id,
          vehicle_price: Number(form.vehicle_price) || 0,
          hypothecation: form.hypothecation_yes ? Number(form.hypothecation) || 0 : 0,
          ext_warranty: form.ext_warranty_yes ? Number(form.ext_warranty) || 0 : 0,
          down_payment: form.down_payment ? Number(form.down_payment) : null,
        });
        setComputed(data.computed);
        if (!form.down_payment) {
          setForm((f) => ({ ...f, down_payment: String(data.computed.down_payment) }));
        }
      } catch { /* live preview is best-effort */ }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scheme_id, form.vehicle_price, form.hypothecation_yes, form.hypothecation, form.ext_warranty_yes, form.ext_warranty, form.down_payment]);

  const pickVehicle = (rid) => {
    const row = pricelist.find((r) => r.id === rid);
    setForm((f) => ({
      ...f,
      vehicle_ref_id: rid,
      vehicle_model: row?.package_name || "",
      vehicle_price: row ? String(row.dealer_price) : f.vehicle_price,
      down_payment: "",
    }));
  };

  const save = async () => {
    if (!form.scheme_id) { toast.error("Pick a finance scheme"); return; }
    if (!Number(form.vehicle_price)) { toast.error("Vehicle price is required"); return; }
    setBusy(true);
    try {
      const body = {
        vehicle_ref_id: form.vehicle_ref_id || null,
        vehicle_model: form.vehicle_model,
        vehicle_price: Number(form.vehicle_price) || 0,
        hypothecation: form.hypothecation_yes ? Number(form.hypothecation) || 0 : 0,
        ext_warranty: form.ext_warranty_yes ? Number(form.ext_warranty) || 0 : 0,
        scheme_id: form.scheme_id,
        down_payment: form.down_payment ? Number(form.down_payment) : null,
        customer: { name: form.customer_name, phone: form.customer_phone, address: form.customer_address },
        branch_name: form.branch_name || "",
        branch_phone: form.branch_phone || "",
        branch_address: form.branch_address || "",
        notes: form.notes,
      };
      if (form.id) await api.patch(`/finance/quotes/${form.id}`, body);
      else await api.post("/finance/quotes", body);
      toast.success("Finance quote saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="finance-quote-editor">
      <div className="bg-canvas border border-canvas-line w-full max-w-3xl max-h-[88vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
            {form.id ? `Edit ${form.serial}` : "New Finance Quote"}
          </div>
          <button onClick={onClose} data-testid="editor-close" className="p-1.5 hover:bg-canvas-panel"><X size={14} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Vehicle</span>
            <select value={form.vehicle_ref_id} onChange={(e) => pickVehicle(e.target.value)} data-testid="fq-vehicle"
              className="px-2 py-2 bg-canvas border border-canvas-line text-sm">
              <option value="">— pick from price list —</option>
              {pricelist.map((r) => (
                <option key={r.id} value={r.id}>{r.package_name} — {inr(r.dealer_price)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Vehicle Price (on-road) ₹</span>
            <input type="number" value={form.vehicle_price} data-testid="fq-price"
              onChange={(e) => setForm((f) => ({ ...f, vehicle_price: e.target.value, down_payment: "" }))}
              className="px-2 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Scheme</span>
            <FinanceSchemePicker
              schemes={schemes.filter((s) => s.active !== false)}
              value={form.scheme_id}
              onChange={(id) => setForm((f) => ({ ...f, scheme_id: id, down_payment: "" }))}
              testId="fq-scheme"
            />
          </label>
          <label className="flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.hypothecation_yes} data-testid="fq-hypo-toggle"
              onChange={(e) => setForm((f) => ({ ...f, hypothecation_yes: e.target.checked, down_payment: "" }))} />
            <span className="text-sm">Hypothecation</span>
            <input type="number" value={form.hypothecation} disabled={!form.hypothecation_yes} data-testid="fq-hypo"
              onChange={(e) => setForm((f) => ({ ...f, hypothecation: e.target.value, down_payment: "" }))}
              className="w-24 px-2 py-1 bg-canvas border border-canvas-line text-sm disabled:opacity-40" />
          </label>
          <label className="flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.ext_warranty_yes} data-testid="fq-warranty-toggle"
              onChange={(e) => setForm((f) => ({ ...f, ext_warranty_yes: e.target.checked, down_payment: "" }))} />
            <span className="text-sm">5Y Extended Warranty</span>
            <input type="number" value={form.ext_warranty} disabled={!form.ext_warranty_yes} data-testid="fq-warranty"
              onChange={(e) => setForm((f) => ({ ...f, ext_warranty: e.target.value, down_payment: "" }))}
              className="w-24 px-2 py-1 bg-canvas border border-canvas-line text-sm disabled:opacity-40" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Down Payment ₹ (auto — editable)</span>
            <input type="number" value={form.down_payment} data-testid="fq-down-payment"
              onChange={(e) => setForm((f) => ({ ...f, down_payment: e.target.value }))}
              className="px-2 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Customer Name</span>
            <input value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} data-testid="fq-customer-name"
              className="px-2 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted">Customer Phone</span>
            <input value={form.customer_phone} onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))} data-testid="fq-customer-phone"
              className="px-2 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          {isAdmin && branches.length > 0 && (
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted">Showroom</span>
              <select
                value={form.branch_name || ""}
                onChange={(e) => {
                  const br = branches.find((x) => (x.name || "").toUpperCase() === e.target.value);
                  setForm((f) => ({
                    ...f,
                    branch_name: e.target.value,
                    branch_phone: br ? [br.phone, br.alternate_phone].filter(Boolean).join(", ") : "",
                    branch_address: br?.address || "",
                  }));
                }}
                data-testid="fq-branch"
                className="px-2 py-2 bg-canvas border border-canvas-line text-sm"
              >
                <option value="">— head office —</option>
                {branches.map((b) => (
                  <option key={b.id} value={(b.name || "").toUpperCase()}>{b.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {computed && (
          <div className="border border-ink/20 bg-canvas-panel/40 p-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="fq-preview">
            {[
              ["Loan Amount", inr(computed.loan_amount)],
              ["Total Interest", inr(computed.total_interest)],
              [`EMI × ${computed.tenure}m`, inr(computed.emi)],
              ["Down Payment", inr(computed.total_downpayment)],
              ["Processing Costs", inr(computed.processing_total)],
            ].map(([label, val], i) => (
              <div key={label}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
                <div className={`font-mono tabular-nums font-semibold ${i === 2 ? "text-2xl text-brand" : "text-lg"}`}
                  data-testid={`fq-preview-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={save} disabled={busy} data-testid="fq-save"
            className="px-5 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-50">
            {busy ? "Saving…" : "Save Quote"}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function FinanceQuotesTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || (user?.permissions || []).includes("quotes.manage");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [pricelist, setPricelist] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showSchemes, setShowSchemes] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/quotes", { params: { q: q || undefined, limit: 200 } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q]);

  const loadSchemes = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/schemes");
      setSchemes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSchemes();
    (async () => {
      try {
        const { data } = await api.get("/pricing/quotes/pricelist");
        setPricelist(data || []);
      } catch { /* price list optional */ }
      try {
        const { data } = await api.get("/pricing/quotes/quote-branches");
        setBranches(data || []);
      } catch { /* branch pick optional */ }
    })();
  }, [loadSchemes]);

  const printPdf = async (row) => {
    try {
      const resp = await api.get(`/finance/quotes/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendWa = async (row) => {
    const mobile = window.prompt("Send to WhatsApp — mobile (10 digits):", row.customer?.phone || "");
    if (!mobile) return;
    try {
      const { data } = await api.post(`/finance/quotes/${row.id}/send-whatsapp`, { mobile });
      if (data.ok) toast.success("WhatsApp sent");
      else toast.error(`WhatsApp failed: ${data.vendor || ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.serial}?`)) return;
    try {
      await api.delete(`/finance/quotes/${row.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (row, status) => {
    try {
      await api.post(`/finance/quotes/${row.id}/status`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (row) => setEditing({
    id: row.id, serial: row.serial,
    vehicle_ref_id: row.vehicle_ref_id || "", vehicle_model: row.vehicle_model || "",
    vehicle_price: String(row.vehicle_price || ""),
    hypothecation: row.hypothecation ?? 500, hypothecation_yes: (row.hypothecation ?? 0) > 0,
    ext_warranty: row.ext_warranty ?? 999, ext_warranty_yes: (row.ext_warranty ?? 0) > 0,
    scheme_id: row.scheme?.id || "", down_payment: String(row.computed?.down_payment || ""),
    customer_name: row.customer?.name || "", customer_phone: row.customer?.phone || "",
    customer_address: row.customer?.address || "",
    branch_name: row.branch_name || "", notes: row.notes || "",
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial / customer / model…"
            data-testid="fq-search"
            className="pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm w-72" />
        </div>
        <button onClick={() => setEditing({ ...EMPTY_FORM })} data-testid="fq-new"
          className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5">
          <Plus size={13} /> New Finance Quote
        </button>
        {canManage && (
          <button onClick={() => setShowSchemes(true)} data-testid="fq-schemes"
            className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink">
            Schemes ({schemes.length})
          </button>
        )}
      </div>

      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Quote", "Date", "Customer", "Vehicle", "Scheme", "Tenure", "EMI", "Down Payment", "Status", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted text-xs">No finance quotes yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-canvas-line/50" data-testid={`fq-row-${r.serial}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.serial}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.quote_date}</td>
                <td className="px-3 py-2">{r.customer?.name || "—"}<div className="text-xs text-ink-muted">{r.customer?.phone}</div></td>
                <td className="px-3 py-2 text-xs">{r.vehicle_model}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.scheme?.product_id}</td>
                <td className="px-3 py-2 font-mono">{r.computed?.tenure}m</td>
                <td className="px-3 py-2 font-mono font-semibold" data-testid={`fq-emi-${r.serial}`}>{inr(r.computed?.emi)}</td>
                <td className="px-3 py-2 font-mono">{inr(r.computed?.total_downpayment)}</td>
                <td className="px-3 py-2">
                  <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`fq-status-${r.serial}`}
                    className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border-0 ${STATUS_COLORS[r.status] || ""}`}>
                    {Object.keys(STATUS_COLORS).map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={() => printPdf(r)} title="PDF" data-testid={`fq-pdf-${r.serial}`} className="p-1 hover:bg-canvas-panel"><Printer size={14} /></button>
                    <button onClick={() => sendWa(r)} title="WhatsApp" data-testid={`fq-wa-${r.serial}`} className="p-1 hover:bg-canvas-panel text-emerald-600"><WhatsappLogo size={14} /></button>
                    <button onClick={() => startEdit(r)} title="Edit" data-testid={`fq-edit-${r.serial}`} className="p-1 hover:bg-canvas-panel"><PencilSimple size={14} /></button>
                    <button onClick={() => remove(r)} title="Delete" data-testid={`fq-delete-${r.serial}`} className="p-1 hover:bg-rose-50 text-rose-600"><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <QuoteEditor
          initial={editing}
          schemes={schemes}
          pricelist={pricelist}
          branches={branches}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showSchemes && <SchemeManager onClose={() => setShowSchemes(false)} onChanged={loadSchemes} />}
    </div>
  );
}
